import {Injectable} from '@angular/core';

import * as AWS from 'aws-sdk';
import {BehaviorSubject} from "rxjs";
import {IAwsKeys} from "./aws-keys.interface";
import {ChecksumAlgorithm, PutObjectCommand, PutObjectCommandOutput, S3Client} from '@aws-sdk/client-s3';
import {Upload} from '@aws-sdk/lib-storage';

@Injectable({
  providedIn: 'root'
})
export class AwsUploadService {

  uploadedBytes: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  totalBytes: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  uploadProgress: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  isUploading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  uploadFailed: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  file: File;
  private _successCallback: () => void;
  private _errorCallback: () => void;
  awsKeys: IAwsKeys;

  constructor() {

  }

  uploadFile(awsKeys: IAwsKeys, file: File, successCallback: () => void, errorCallback: () => void) {
    this.awsKeys = awsKeys;
    this.file = file;
    const s3 = new S3Client({
      region: 'eu-central-1',
      credentials: {
        accessKeyId: this.awsKeys.sts_credentials.AccessKeyId,
        secretAccessKey: this.awsKeys.sts_credentials.SecretAccessKey,
        sessionToken: this.awsKeys.sts_credentials.SessionToken,
      },
    });
    this._successCallback = successCallback;
    this._errorCallback = errorCallback;

    const params = {
      Bucket: awsKeys.bucket,
      Key: awsKeys.file_path,
      Body: file,
      ContentType: file.type,
    };

    const upload = new Upload({
      client: s3,
      params,
      partSize: 100 * 1024 * 1024, // 100MB
      queueSize: 5,
    });

    upload.on('httpUploadProgress', (progress) => {
      const uploadedBytes = progress.loaded;
      const totalBytes = progress.total;
      this.uploadedBytes.next(progress.loaded);
      this.totalBytes.next(progress.total);
      const uploadProgress = (uploadedBytes / totalBytes) * 100;
      this.uploadProgress.next(Math.floor(uploadProgress));
      this.isUploading.next(uploadProgress !== 100);
    });

    upload.done()
        .then(() => this._successCallback())
        .catch((err) => {
          this.isUploading.next(false);
          this.uploadFailed.next(true);
          this._errorCallback();
        });
  }

  async simpleFileUpload(awsKeys: IAwsKeys, file: File, region: string) {
    this.awsKeys = awsKeys;
    this.file = file;
    const s3 = new S3Client({
      region: region,
      credentials: {
        accessKeyId: this.awsKeys.sts_credentials.AccessKeyId,
        secretAccessKey: this.awsKeys.sts_credentials.SecretAccessKey,
        sessionToken: this.awsKeys.sts_credentials.SessionToken,
      },
    });
    const file_path = this.generateS3Key(file.type.split('/')[1]?.toLowerCase() || 'png');
    const params = new PutObjectCommand({
      Bucket: awsKeys.bucket,
      Key: file_path,
      Body: file,
      ContentType: file.type,
      ACL: 'public-read'
    });

    const response: PutObjectCommandOutput = await s3.send(params);
    console.log('Upload successful:', response);

    // --- CONSTRUCT THE URL MANUALLY ---
    // The standard virtual hosted-style URL format:
    // https://BUCKET_name.s3.region.amazonaws.com
    const url = `https://${awsKeys.bucket}.s3.${region}.amazonaws.com/${file_path}`;
    return url;
  }

  generateS3Key(ext = '.png'): string {
    const now = new Date();

    // Format date as YYYY/MM/DD
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0'); // months 0-11
    const day = String(now.getDate()).padStart(2, '0');

    const datePath = `${year}/${month}/${day}`;

    const uniqueId = `prompt_${Date.now().toString(16)}_${Math.floor(Math.random() * 1e8)}`;

    const key = `prompts/${datePath}/${uniqueId}.${ext}`;

    return key;
  }

  retryUpload() {
    this.resetProps();
    this.uploadFile(this.awsKeys, this.file, this._successCallback, this._errorCallback);
  }

  resetProps() {
    this.uploadFailed.next(false);
    this.isUploading.next(false);
    this.uploadedBytes.next(0);
    this.totalBytes.next(0);
    this.uploadProgress.next(0);
  }
}
